/**
 * The processor's card fields (through the adapter registry) with a submit
 * button: tokenizes and hands the one-time token to onToken. Used by the
 * staff add-card dialog.
 */
import React, { useState } from 'react';
import * as types from '../../api/types';
import { primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { usePaymentForm } from './usePaymentForm';
import { PaymentFormError } from './adapters/paymentFormAdapter';
import { getErrorMessage } from '../../utils/apiError';
import { Toast } from '../../components/Toast';

interface Props {
  clientConfig: types.PaymentClientConfig;
  submitLabel: string;
  intent?: 'CHARGE' | 'STORE';
  children?: React.ReactNode;
  onToken: (sourceToken: string) => Promise<void>;
  onCancel: () => void;
}

export function CardEntryForm({ clientConfig, submitLabel, intent = 'STORE', children, onToken, onCancel }: Props) {
  const form = usePaymentForm(clientConfig);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { sourceToken } = await form.tokenize({ intent });
      await onToken(sourceToken);
    } catch (cause) {
      setError(cause instanceof PaymentFormError ? cause.message : getErrorMessage(cause, 'The card could not be saved'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)} className="space-y-3" noValidate>
      <div ref={form.containerRef} data-testid="card-form" className="min-h-[3rem]" />
      {form.status === 'loading' && <p className="text-sm text-gray-500">Loading the secure card form…</p>}
      {form.error && <Toast kind="error">{form.error}</Toast>}
      {children}
      {error && <Toast kind="error">{error}</Toast>}
      <div className="flex flex-wrap gap-3">
        <button type="submit" className={primaryButtonClass} disabled={busy || form.status !== 'ready'}>{busy ? 'Saving…' : submitLabel}</button>
        <button type="button" className={secondaryButtonClass} disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
